/* eslint-disable */
/* ============================================================
   AsciiPortrait v4 — Kevin's ASCII face with live face FX
   ------------------------------------------------------------
   v3 changes vs v2:
   - More violent explosion: higher force, larger radius, faster
     spring-back, slight angular jitter.
   - REAL HEADSHOT peeks through under the ASCII layer.
     The headshot is hidden by default (opacity 0). On pointer
     enter, a radial mask reveals it ONLY around the cursor —
     so as ASCII chars are pushed away, the photo is visible
     in the gap they leave.
   - Mouse-out: glyphs spring back, mask shrinks, photo fades.
   ------------------------------------------------------------
   Usage:
     <window.AsciiPortrait
       art={KEVIN_ASCII}
       photo="assets/headshot.jpg"
       fontSize={11}
       lineHeight={1.05}
       letterSpacing={0.05}
       force={140}            // v3: was 70
       radius={130}           // v3: was 90
       returnSpring={0.20}    // v3: snappier
       jitter={0.4}           // v3: angular noise on the push
       revealRadius={120}     // photo mask radius (px)
       label="ID · 0001 / Kevin Zicherman"
     />
   ============================================================ */

import React, { useEffect as _useEffectAP, useRef as _useRefAP, useMemo as _useMemoAP, useState as _useStateAP } from 'react';

const KEVIN_ASCII_DEFAULT = (typeof window !== "undefined" && window.KEVIN_ASCII) ? window.KEVIN_ASCII : "";

let _apIdCounter = 0;
const AP_EYE_ROW_MIN = 6;
const AP_EYE_ROW_MAX = 8;
const AP_BREATH_MS = 4000;
const AP_SCAN_MS = 8000;
const AP_SCAN_DURATION_MS = 1500;
const AP_SHOCK_RADIUS = 200;
const AP_SHOCK_BASE_FORCE = 10;
const AP_DWELL_DELAY = 400;
const AP_DWELL_EXPAND_PX_PER_MS = 0.03;
const AP_DWELL_RAMP = ".,:-=+*xX#@";
const AP_SCAN_COLOR = "#5fbfb9";

function injectAsciiPortraitStyles() {
  if (typeof document === "undefined" || document.getElementById("ap-face-fx-style")) return;
  const styleEl = document.createElement("style");
  styleEl.id = "ap-face-fx-style";
  styleEl.textContent = `
    @keyframes ap-mobile-breath {
      0%, 100% { transform: scale(0.992); opacity: 0.82; filter: contrast(1.08) brightness(0.92); }
      50% { transform: scale(1.014); opacity: 1; filter: contrast(1.18) brightness(1.08); }
    }
    .ap-desktop-only { display: inline-block; max-width: 100%; }
    .ascii-mobile-only.ap-fallback {
      display: none !important;
      text-align: center;
      width: 100%;
      max-width: 100%;
    }
    .ascii-mobile-only.ap-fallback .ap-mobile-breath {
      width: var(--portrait-mobile-size, clamp(180px, 64vw, 240px));
      max-width: 100%;
      height: auto;
      display: inline-block;
      transform-origin: 50% 50%;
      animation: ap-mobile-breath 4s ease-in-out infinite;
      mix-blend-mode: screen;
    }
    @media (max-width: 768px), (pointer: coarse) {
      .ap-desktop-only { display: none !important; }
      .ascii-mobile-only.ap-fallback { display: block !important; }
    }
    @media (prefers-reduced-motion: reduce) {
      .ascii-mobile-only.ap-fallback .ap-mobile-breath {
        animation: none !important;
        transform: none !important;
        opacity: 0.94;
      }
    }
  `;
  document.head.appendChild(styleEl);
}

function compactAsciiArt(art) {
  const raw = String(art || "").split("\n");
  const first = raw.findIndex((line) => line.trim().length > 0);
  if (first === -1) return "";
  let last = raw.length - 1;
  while (last > first && raw[last].trim().length === 0) last--;

  const body = raw.slice(first, last + 1);
  const minIndent = body.reduce((min, line) => {
    if (!line.trim()) return min;
    const match = line.match(/^ */);
    return Math.min(min, match ? match[0].length : 0);
  }, Infinity);

  return body.map((line) => line.slice(minIndent).trimEnd()).join("\n");
}

/* F-07: MobileBreathPortrait
   Renders the animated WebP when opt-in is active, falls back to ascii-face.png.
   Opt-in: ?breath=1 URL param (per-visit) OR localStorage kz_breath=1 (persistent).
   When the WebP asset doesn't exist, onerror swaps back to the static PNG silently.
*/
function MobileBreathPortrait({ alt }) {
  const useWebP = _useMemoAP(() => {
    if (typeof window === "undefined") return false;
    const urlParam = new URLSearchParams(window.location.search).get("breath") === "1";
    const stored = localStorage.getItem("kz_breath") === "1";
    return urlParam || stored;
  }, []);

  const [src, setSrc] = _useStateAP(
    useWebP ? "assets/portrait-breath.webp" : "assets/ascii-face.png"
  );

  // Persist ?breath=1 param to localStorage for next visit
  _useEffectAP(() => {
    if (typeof window === "undefined") return;
    if (new URLSearchParams(window.location.search).get("breath") === "1") {
      try { localStorage.setItem("kz_breath", "1"); } catch (_) {}
    }
  }, []);

  return (
    <img
      className="ap-mobile-breath"
      src={src}
      alt={alt || "Kevin Zicherman ASCII portrait"}
      loading="eager"
      onError={() => {
        if (src !== "assets/ascii-face.png") setSrc("assets/ascii-face.png");
      }}
    />
  );
}

function AsciiPortrait({
  compact = false,
  art = KEVIN_ASCII_DEFAULT,
  photo = "", // default: no photo reveal · opt-in only · ASCII ripple stands alone
  fontSize = compact ? 6.5 : 11,
  lineHeight = compact ? 1 : 1.05,
  letterSpacing = compact ? 0 : 0.05,
  force = compact ? 70 : 140,           // v3 default — much more violent
  radius = compact ? 62 : 130,
  returnSpring = compact ? 0.24 : 0.20,
  damping = 0.74,
  jitter = compact ? 0.25 : 0.4,
  revealRadius = compact ? 54 : 120,
  glow = !compact,
  label = compact ? "" : "ID · 0001 / Kevin Zicherman",
  className = "",
  style = {}
}) {
  const wrapRef = _useRefAP(null);
  const stageRef = _useRefAP(null);
  const photoRef = _useRefAP(null);
  const turbRef = _useRefAP(null);   // feTurbulence element for portal warp
  const dispRef = _useRefAP(null);   // feDisplacementMap element
  const charsRef = _useRefAP([]);     // [{el, ox, oy, x, y, vx, vy, jx, jy}]
  const mouseRef = _useRefAP({ x: -9999, y: -9999, active: false, t: 0, stillSince: 0, dwellVersion: 0 });
  const rafRef = _useRefAP(0);
  const cellRef = _useRefAP({ w: fontSize * 0.6, h: fontSize * lineHeight, rows: 1 });
  const [stageDims, setStageDims] = _useStateAP({ w: 0, h: 0 });
  /* stable unique filter id — computed once at construction, not on every render */
  const filterIdRef = _useRefAP(null);
  if (!filterIdRef.current) filterIdRef.current = "ap-portal-" + (++_apIdCounter);
  const filterId = filterIdRef.current;

  const normalizedArt = _useMemoAP(() => compact ? compactAsciiArt(art) : art, [art, compact]);
  const lines = _useMemoAP(() => normalizedArt.split("\n"), [normalizedArt]);

  _useEffectAP(() => {
    injectAsciiPortraitStyles();
  }, []);

  /* mount: build positioned spans */
  _useEffectAP(() => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.innerHTML = "";
    const chars = [];

    /* measure actual char width with a probe */
    const probe = document.createElement("span");
    probe.textContent = "M";
    probe.style.cssText = `position:absolute;visibility:hidden;font:${fontSize}px var(--mono);letter-spacing:${letterSpacing}em;`;
    stage.appendChild(probe);
    const cw = probe.getBoundingClientRect().width || (fontSize * 0.6);
    stage.removeChild(probe);
    const ch = fontSize * lineHeight;
    const firstFaceRow = Math.max(0, lines.findIndex((line) => line.trim().length > 0));
    let lastFaceRow = lines.length - 1;
    while (lastFaceRow > firstFaceRow && lines[lastFaceRow].trim().length === 0) lastFaceRow--;
    const faceRows = Math.max(1, lastFaceRow - firstFaceRow + 1);
    cellRef.current = { w: cw, h: ch, rows: faceRows };

    let maxCols = 0;
    lines.forEach((line, row) => {
      maxCols = Math.max(maxCols, line.length);
      for (let col = 0; col < line.length; col++) {
        const c = line[col];
        if (c === " " || c === "\t") continue;
        const span = document.createElement("span");
        span.textContent = c;
        const ox = col * cw;
        const oy = row * ch;
        const faceRow = Math.max(0, Math.min(faceRows - 1, row - firstFaceRow));
        const seed = Math.sin((row + 1) * 12.9898 + (col + 1) * 78.233) * 43758.5453;
        const seedNorm = seed - Math.floor(seed);
        span.style.cssText =
          `position:absolute;left:0;top:0;` +
          `font:${fontSize}px var(--mono);` +
          `letter-spacing:${letterSpacing}em;` +
          `line-height:1;` +
          `color:var(--fg);` +
          `transform:translate3d(${ox}px,${oy}px,0);` +
          `will-change:transform,opacity;` +
          `pointer-events:none;` +
          `transition:color 240ms ease;` +
          `z-index:2;`;
        stage.appendChild(span);
        chars.push({
          el: span,
          base: c,
          ox, oy,
          x: ox, y: oy,
          vx: 0, vy: 0,
          row,
          col,
          faceRow,
          isEye: faceRow >= AP_EYE_ROW_MIN && faceRow <= AP_EYE_ROW_MAX,
          phase: seedNorm * Math.PI * 2,
          rampIndex: Math.floor(seedNorm * AP_DWELL_RAMP.length),
          dwellVersion: -1,
          dwellAt: 0,
          dwellOn: false,
          shown: c,
          color: "var(--fg)",
          /* per-char angular jitter seed for non-uniform explosion */
          jx: (Math.sin(row * 12.9898 + col * 78.233) * 43758.5453) % 1,
          jy: (Math.cos(row * 39.346 + col * 11.135) * 23421.6311) % 1,
        });
      }
    });

    const w = maxCols * cw;
    const h = lines.length * ch;
    stage.style.width = w + "px";
    stage.style.height = h + "px";
    setStageDims({ w, h });
    charsRef.current = chars;
  }, [lines, fontSize, lineHeight, letterSpacing]);

  /* pointer + RAF loop */
  _useEffectAP(() => {
    const wrap = wrapRef.current;
    const stage = stageRef.current;
    const photoEl = photoRef.current;
    if (!wrap || !stage) return;

    /* On touch/coarse-pointer devices (phones/tablets) there is no true hover.
       Pointer events fire on tap, which causes a jarring one-frame scatter and
       an immediate spring-back. Disable the interactive effects entirely on
       coarse-pointer devices — the ASCII art still renders; it stays static. */
    const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
    if (isCoarsePointer) {
      /* Still run a minimal RAF loop so chars settle at origin on first frame */
      const settle = () => {
        const chars = charsRef.current;
        let moved = false;
        for (let i = 0; i < chars.length; i++) {
          const c = chars[i];
          if (Math.abs(c.x - c.ox) > 0.1 || Math.abs(c.y - c.oy) > 0.1) {
            c.x += (c.ox - c.x) * 0.25;
            c.y += (c.oy - c.y) * 0.25;
            c.el.style.transform = `translate3d(${c.x.toFixed(2)}px,${c.y.toFixed(2)}px,0)`;
            moved = true;
          }
        }
        if (moved) rafRef.current = requestAnimationFrame(settle);
      };
      rafRef.current = requestAnimationFrame(settle);
      return () => cancelAnimationFrame(rafRef.current);
    }

    const reduceQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reducedMotion = reduceQuery.matches;
    const onReducedMotionChange = (event) => {
      reducedMotion = event.matches;
    };
    if (reduceQuery.addEventListener) reduceQuery.addEventListener("change", onReducedMotionChange);
    else if (reduceQuery.addListener) reduceQuery.addListener(onReducedMotionChange);

    let revealAlpha = 0;     // 0..1, drives photo mask softness
    const REVEAL_IN = 0.12;
    const REVEAL_OUT = 0.45;  // snappy fade-out so no ghost glow lingers
    let nextBlink = performance.now() + 4200 + Math.random() * 2600;
    let blinkUntil = 0;

    /* scan-line sweep state */
    let scanStartTime = -1;  // performance.now() when current sweep started; -1 = idle
    let nextScanTime = performance.now() + AP_SCAN_MS;
    let isVisible = true;    // IntersectionObserver tracks face visibility

    const observer = new IntersectionObserver((entries) => {
      isVisible = entries[0].isIntersecting;
    }, { threshold: 0.1 });
    observer.observe(wrap);

    /* portal warp state — eases the turbulence scale in/out */
    let portalAlpha = 0;    // 0..1
    const PORTAL_IN = 0.06;
    const PORTAL_OUT = 0.08;
    const PORTAL_BASE_FREQ = 0.018;  // subtle — operator terminal, not psychedelic
    const PORTAL_SCALE = 8;

    const onMove = (e) => {
      const rect = stage.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const mouse = mouseRef.current;
      const moved = !mouse.active || Math.abs(mx - mouse.x) > 1.5 || Math.abs(my - mouse.y) > 1.5;
      mouse.x = mx;
      mouse.y = my;
      mouse.active = true;
      if (moved) {
        mouse.stillSince = performance.now();
        mouse.dwellVersion += 1;
      }
      const wrapRect = wrap.getBoundingClientRect();
      wrap.style.setProperty("--mx", (e.clientX - wrapRect.left) + "px");
      wrap.style.setProperty("--my", (e.clientY - wrapRect.top) + "px");
    };
    const onEnter = (e) => {
      onMove(e);
      // Tell the page to dim the global cursor spotlight while the portrait
      // owns the focus — prevents double-glow on the face and a lingering
      // amber blob right where the face is when you move out.
      document.body.setAttribute("data-portrait-hover", "1");
    };
    const onLeave = () => {
      mouseRef.current.active = false;
      mouseRef.current.x = -9999;
      mouseRef.current.y = -9999;
      mouseRef.current.stillSince = 0;
      mouseRef.current.dwellVersion += 1;
      // park the CSS pointer vars off-screen so any --mx/--my-driven
      // overlays don't leave a lingering glow over the face
      wrap.style.setProperty("--mx", "-9999px");
      wrap.style.setProperty("--my", "-9999px");
      document.body.removeAttribute("data-portrait-hover");
    };
    const onDown = (e) => {
      onMove(e);
      if (reducedMotion) return;
      const rect = stage.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const chars = charsRef.current;
      const shockR2 = AP_SHOCK_RADIUS * AP_SHOCK_RADIUS;
      for (let i = 0; i < chars.length; i++) {
        const c = chars[i];
        const dx = c.x - px;
        const dy = c.y - py;
        const d2 = dx * dx + dy * dy;
        if (d2 < shockR2 && d2 > 0.01) {
          const d = Math.sqrt(d2);
          const impulse = AP_SHOCK_BASE_FORCE * (1 - d / AP_SHOCK_RADIUS) * 1.5;
          c.vx += (dx / d) * impulse;
          c.vy += (dy / d) * impulse;
        }
      }
    };

    wrap.addEventListener("pointermove", onMove);
    wrap.addEventListener("pointerleave", onLeave);
    wrap.addEventListener("pointerenter", onEnter);
    wrap.addEventListener("pointerdown", onDown);

    const tick = () => {
      const chars = charsRef.current;
      const now = performance.now();
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const active = mouseRef.current.active;
      const r2 = radius * radius;
      mouseRef.current.t += 1;
      const t = mouseRef.current.t;
      if (!reducedMotion && now > nextBlink && now > blinkUntil) {
        blinkUntil = now + 80;
        nextBlink = now + 4000 + Math.random() * 3000;
      }
      const blinking = false; // removed: caused black-bar flash on eyes
      const breathCycle = !reducedMotion ? (now % AP_BREATH_MS) / AP_BREATH_MS * Math.PI * 2 : 0;
      const faceRows = cellRef.current.rows;

      /* scan-line sweep: schedule + advance */
      let scanRow = -999;
      if (!reducedMotion && isVisible) {
        if (scanStartTime < 0 && now >= nextScanTime) {
          scanStartTime = now;
        }
        if (scanStartTime >= 0) {
          const elapsed = now - scanStartTime;
          if (elapsed <= AP_SCAN_DURATION_MS) {
            scanRow = (elapsed / AP_SCAN_DURATION_MS) * faceRows;
          } else {
            /* sweep finished — reset */
            scanStartTime = -1;
            nextScanTime = now + AP_SCAN_MS;
          }
        }
      }
      const dwellAge = active ? now - mouseRef.current.stillSince - AP_DWELL_DELAY : -1;
      const dwellActive = !reducedMotion && active && dwellAge >= 0;
      const dwellRadius = dwellActive ? dwellAge * AP_DWELL_EXPAND_PX_PER_MS : 0;
      const dwellR2 = dwellRadius * dwellRadius;
      const cellW = cellRef.current.w;
      const cellH = cellRef.current.h;
      const dwellVersion = mouseRef.current.dwellVersion;

      for (let i = 0; i < chars.length; i++) {
        const c = chars[i];
        let fx = 0, fy = 0;

        if (active) {
          const dx = c.x - mx;
          const dy = c.y - my;
          const d2 = dx * dx + dy * dy;
          if (d2 < r2 && d2 > 0.01) {
            const d = Math.sqrt(d2);
            const falloff = 1 - d / radius;
            /* v3: punchier curve (cubic falloff -> sharper near cursor) */
            const f = force * falloff * falloff * falloff;
            const nx = dx / d;
            const ny = dy / d;
            /* angular jitter: rotate the push vector by a small per-char angle
               that wobbles over time, so chars don't all fly along radial lines */
            const angle = (c.jx + Math.sin(t * 0.04 + c.jy * 6.28) * 0.3) * jitter;
            const cs = Math.cos(angle), sn = Math.sin(angle);
            const rx = nx * cs - ny * sn;
            const ry = nx * sn + ny * cs;
            fx += rx * f * 0.08;
            fy += ry * f * 0.08;
          }
        }

        /* spring back */
        const sx = (c.ox - c.x) * returnSpring;
        const sy = (c.oy - c.y) * returnSpring;

        c.vx = (c.vx + fx + sx) * damping;
        c.vy = (c.vy + fy + sy) * damping;
        c.x += c.vx;
        c.y += c.vy;

        let renderX = c.x;
        let renderY = c.y;
        if (!reducedMotion) {
          renderX += Math.sin(breathCycle + c.phase) * 0.5;
          renderY += Math.cos(breathCycle + c.phase * 1.3) * 0.5;
        }
        const scanDelta = Math.abs(c.faceRow - scanRow);
        const scanHit = scanRow >= 0 && !reducedMotion && scanDelta <= 2;
        const scanBright = scanRow >= 0 && !reducedMotion && scanDelta <= 0.45;
        if (scanHit) {
          /* y-jitter: ±1px random offset driven by per-char phase + time */
          renderY += Math.sin(now * 0.08 + c.phase) * (1 - scanDelta / 3);
        }

        c.el.style.transform = `translate3d(${renderX.toFixed(2)}px,${renderY.toFixed(2)}px,0)`;

        /* fade out chars that have wandered far (so the face peek isn't
           crowded by displaced chars hovering over the photo) */
        const distX = c.x - c.ox;
        const distY = c.y - c.oy;
        const dist = Math.sqrt(distX * distX + distY * distY);
        const fade = Math.max(0, 1 - dist / 60);
        let opacity = fade;
        if (scanBright) opacity = Math.max(opacity, 0.9);
        if (blinking && c.isEye) opacity = Math.min(opacity, 0.15);
        c.el.style.opacity = opacity.toFixed(2);

        let nextGlyph = c.base;
        let dwellOn = false;
        if (dwellActive) {
          const ddx = c.ox - mx;
          const ddy = c.oy - my;
          const d2 = ddx * ddx + ddy * ddy;
          const underCursor = Math.abs(ddx) <= cellW * 0.58 && Math.abs(ddy) <= cellH * 0.8;
          if (underCursor || d2 <= dwellR2) {
            if (c.dwellVersion !== dwellVersion) {
              c.dwellVersion = dwellVersion;
              c.dwellAt = now;
            }
            const glyphAge = now - c.dwellAt;
            if (glyphAge < 240) {
              const stageIndex = Math.min(2, Math.floor(glyphAge / 80));
              nextGlyph = AP_DWELL_RAMP[(c.rampIndex + stageIndex) % AP_DWELL_RAMP.length];
            }
            dwellOn = true;
          }
        }
        c.dwellOn = dwellOn;
        if (c.shown !== nextGlyph) {
          c.el.textContent = nextGlyph;
          c.shown = nextGlyph;
        }

        const nextColor = dwellOn ? "var(--amber)" : scanHit ? AP_SCAN_COLOR : "var(--fg)";
        if (c.color !== nextColor) {
          c.el.style.color = nextColor;
          c.color = nextColor;
        }
      }

      /* photo reveal mask — eased in/out */
      if (photoEl) {
        const target = active ? 1 : 0;
        revealAlpha += (target - revealAlpha) * (target > revealAlpha ? REVEAL_IN : REVEAL_OUT);
        photoEl.style.opacity = revealAlpha.toFixed(3);
        const px = active ? mx : stageDims.w / 2;
        const py = active ? my : stageDims.h / 2;
        photoEl.style.webkitMaskImage =
          `radial-gradient(${revealRadius}px ${revealRadius}px at ${px}px ${py}px, ` +
          `rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0) 70%)`;
        photoEl.style.maskImage = photoEl.style.webkitMaskImage;
      }

      /* portal warp — animate feTurbulence baseFrequency + feDisplacementMap scale */
      const turbEl = turbRef.current;
      const dispEl = dispRef.current;
      if (turbEl && dispEl) {
        const portalTarget = active ? 1 : 0;
        portalAlpha += (portalTarget - portalAlpha) * (portalTarget > portalAlpha ? PORTAL_IN : PORTAL_OUT);
        if (portalAlpha > 0.002) {
          /* slowly drift the seed so the warp ripples even when cursor is still */
          const drift = t * 0.003;
          const freq = PORTAL_BASE_FREQ + Math.sin(drift) * 0.004;
          const freqY = PORTAL_BASE_FREQ * 1.4 + Math.cos(drift * 1.3) * 0.003;
          turbEl.setAttribute("baseFrequency", freq.toFixed(5) + " " + freqY.toFixed(5));
          turbEl.setAttribute("seed", Math.floor(t * 0.02) % 99);
          dispEl.setAttribute("scale", (PORTAL_SCALE * portalAlpha).toFixed(2));
        } else {
          dispEl.setAttribute("scale", "0");
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      wrap.removeEventListener("pointermove", onMove);
      wrap.removeEventListener("pointerleave", onLeave);
      wrap.removeEventListener("pointerenter", onEnter);
      wrap.removeEventListener("pointerdown", onDown);
      if (reduceQuery.removeEventListener) reduceQuery.removeEventListener("change", onReducedMotionChange);
      else if (reduceQuery.removeListener) reduceQuery.removeListener(onReducedMotionChange);
      observer.disconnect();
    };
  }, [force, radius, returnSpring, damping, jitter, revealRadius, stageDims.w, stageDims.h]);

  const inset = compact ? 0 : 14;
  const mobileAlt = label ? `${label.replace(/\s*\/\s*/g, " / ")} ASCII portrait` : "Kevin Zicherman ASCII portrait";

  return (
    <React.Fragment>
      {/* F-07: mobile WebP breath loop scaffold
           Asset: /assets/portrait-breath.webp
           Specs (TODO — asset not yet created):
             - 240×320px (matches --portrait-mobile-size max)
             - Animated WebP, 8fps, 4s loop, ~2 subtle scale/brightness breath cycles
             - Max file size: 120KB (low-bitrate, OLED-safe near-black BG)
             - Format: animated WebP (supported iOS 14+, Chrome, Firefox)
           Enable via: ?breath=1 URL param OR localStorage kz_breath=1
           Falls back to ascii-face.png if asset missing (onerror).
      */}
      <div className="ascii-mobile-only ap-fallback" aria-hidden={compact ? "true" : undefined}>
        <MobileBreathPortrait alt={compact ? "" : mobileAlt} />
      </div>
      <div className="ascii-desktop-only ap-desktop-only">
        <div
          ref={wrapRef}
          className={["ascii-portrait", compact ? "compact" : "", className].filter(Boolean).join(" ")}
          data-compact={compact ? "true" : "false"}
          data-glow={glow ? "on" : "off"}
          style={{
            position: "relative",
            border: compact ? "none" : "1px solid var(--line-strong)",
            padding: inset,
            background: compact ? "transparent" : "var(--bg-1)",
            cursor: "crosshair",
            display: "inline-block",
            ...style
          }}>
          {/* SVG filter: portal warp (feTurbulence + feDisplacementMap) */}
          <svg aria-hidden="true" style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}>
            <defs>
              <filter id={filterId} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
                <feTurbulence
                  ref={turbRef}
                  type="turbulence"
                  baseFrequency="0.018 0.025"
                  numOctaves="3"
                  seed="0"
                  result="turb"
                />
                <feDisplacementMap
                  ref={dispRef}
                  in="SourceGraphic"
                  in2="turb"
                  scale="0"
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            </defs>
          </svg>
          {/* hidden REAL photo, revealed by mask under cursor — opt-in via `photo` prop */}
          {photo ? (
          <div
            ref={photoRef}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: inset, top: inset,
              width: stageDims.w || (compact ? "100%" : "calc(100% - 28px)"),
              height: stageDims.h || (compact ? "100%" : "calc(100% - 28px)"),
              backgroundImage: `url("${photo}")`,
              backgroundSize: "cover",
              backgroundPosition: "center 30%",
              opacity: 0,
              filter: "contrast(1.4) brightness(1.1) sepia(0.4) hue-rotate(170deg) saturate(1.3)",
              mixBlendMode: "screen",
              pointerEvents: "none",
              zIndex: 1,
              transition: "filter 200ms ease"
            }}
          />
          ) : null}
          <div
            ref={stageRef}
            className="ap-stage"
            style={{
              position: "relative",
              zIndex: 2,
              filter: `url(#${filterId})`
            }}
          />
          {/* corner brackets + REC label */}
          {!compact && <span style={{ position: "absolute", top: 6, right: 12, fontSize: 9, color: "var(--red)", letterSpacing: "0.2em", animation: "blink 1.6s steps(2,start) infinite", zIndex: 4 }}>● REC</span>}
          {label && (
            <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--fg-4)", letterSpacing: "0.15em", textTransform: "uppercase", position: "relative", zIndex: 4 }}>
              <span>{label.split("/")[0]?.trim()}</span>
              <span>{label.split("/")[1]?.trim() || ""}</span>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

Object.assign(window, { AsciiPortrait });

export { AsciiPortrait };
